import {
  Component,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  ViewChild,
  Output,
  EventEmitter,
  ElementRef
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { StudentsListService } from './students-list.service';
import { ACTION_PROPS_STUDENTS, BUTTON_ACTIONS_STUDENTS } from '../assets/constants';
import {
  DynamicFormComponent,
  EditTagsComponent,
  ModalComponent,
  PersonalizedEmailComponent,
  QuestionBase,
  TagsQuestion
} from 'core/components';
import { StudentsService, JobsService, PopUpService, ProfileService, TrailService, StoreService } from 'core/services';
import { Group } from 'core/models/group';
import { Subscription } from 'rxjs';

@Component({
  selector: 'lms-students-list',
  templateUrl: './students-list.component.html',
  styleUrls: ['./students-list.component.scss'],
  providers: [StudentsListService]
})
export class StudentsListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() listId: string;
  @Input() selectable: boolean;
  @Input() options: any;
  @Input() showActions?: boolean;
  @Input() enrollmentGroup: Group;

  @Output() onEnroll = new EventEmitter<any>();

  public dataSource: any;

  public listOptions: any;
  public selectedStudents: any[] = [];
  public excludedStudents = new Set();
  public isAnySelected = false;
  public disabledButton = false;
  public jobs: any;
  public action: string = '';
  public isHeaderSelectedAll: boolean = false; // Selection of visible and non-visible rows
  public clientId: string;
  public actionItems: any[] = [];
  public actionProps: any = ACTION_PROPS_STUDENTS;
  public enrollmentQuestions: QuestionBase<any>[] = [];

  public studentsToEnroll: any[] = [];
  public user: any;

  @ViewChild('studentsTable', { static: false }) public studentsTable: ElementRef;
  @ViewChild('activateModal', { static: false }) public activateModal: ModalComponent;
  @ViewChild('enrollModal', { static: false }) public enrollModal: ModalComponent;
  @ViewChild('licenseEditTags', { static: false }) public licenseEditTags: EditTagsComponent;
  @ViewChild('personalizedEmail', { static: false }) public personalizedEmail: PersonalizedEmailComponent;
  @ViewChild(DynamicFormComponent, { static: false }) public dynamicForm: DynamicFormComponent;

  private popUpSubscription: Subscription = new Subscription();
  private loggedUserSubscription: Subscription = new Subscription();

  constructor(
    public studentsListService: StudentsListService,
    public studentsService: StudentsService,
    public router: Router,
    public translate: TranslateService,
    public jobsService: JobsService,
    public popupService: PopUpService,
    public profileService: ProfileService,
    public trailService: TrailService
  ) {
    this.jobs = this.jobsService.jobTypes;
  }

  ngOnInit() {
    this.actionItems = this.filterActionItems(BUTTON_ACTIONS_STUDENTS);
    this.clientId = this.profileService.getFixedClientId();
    this.loggedUserSubscription = this.profileService.getLoggedUser$().subscribe((loggedUser) => {
      this.user = loggedUser;
    });
  }

  async ngOnChanges() {
    const trail = this.trailService.getTrailFor(this.listId);
    this.listOptions = { ...this.options, ...trail };
    if (this.listOptions.selected) {
      this.listOptions.selectionHandler = (data) => this.selectionHandler(data);
    }
    this.actionItems = this.filterActionItems(BUTTON_ACTIONS_STUDENTS);

    this.dataSource = await this.studentsListService.getDatasource(this.listId, this.listOptions);
    if (this.listOptions.selected) {
      this.selectedStudents = this.dataSource.docs;
      this.isAnySelected = true;
      this.isHeaderSelectedAll = true;
    }
  }

  ngOnDestroy() {
    this.popUpSubscription.unsubscribe();
    this.loggedUserSubscription.unsubscribe();
    StoreService.remove(this.listId);
  }

  onDatasourceUpdate() {
    StoreService.update(this.listId, this.dataSource);
    this.setSelectAll(false);
  }

  selectionHandler(rowData) {
    if (rowData) {
      if (this.excludedStudents.has(rowData.id)) return false;
      return (
        this.isHeaderSelectedAll ||
        (this.selectedStudents && this.selectedStudents.some((student) => student.id === rowData.id))
      );
    }
    return this.isHeaderSelectedAll;
  }

  onClickRow(student) {
    if (student && !student.suspended) {
      this.router.navigate(['/students', student.id]);
    }
  }

  onRowSelectionChanged(eventDetail) {
    const { selected, data } = eventDetail;
    if (selected) {
      this.excludedStudents.delete(data.id);
    } else {
      this.excludedStudents.add(data.id);
    }
  }

  onSelectionChanged(selection) {
    const { selected = [], isSelectedAll = false } = selection || {};
    if (isSelectedAll && !this.isHeaderSelectedAll) this.excludedStudents.clear();
    this.selectedStudents = selected;
    this.isAnySelected = this.selectedStudents && this.selectedStudents.length ? true : false;
  }

  onFiltersChanged() {
    this.setSelectAll(false);
  }

  setSelectAll(flag) {
    this.dataSource.selectionHandler = flag ? (data) => this.selectionHandler(data) : null;
    this.excludedStudents.clear();
    this.selectedStudents = flag ? this.dataSource.docs : [];
    this.isAnySelected = flag;
    this.isHeaderSelectedAll = flag;
    this.studentsTable.nativeElement.setAllSelected(flag);
  }

  enabledButton() {
    this.disabledButton = !this.disabledButton;
  }

  selectAction(key) {
    switch (key) {
      case '1':
        this.licenseEditTags.openModal();
        break;
      case '2':
        this.personalizedEmail.openModal();
        break;
      case '3':
        this.openModal(this.jobs.ANONYMIZE_USERS_BULK);
        break;
      case '4':
        this.openModal(this.jobs.DELETE_USERS_BULK);
        break;
      case '5':
        this.action = this.jobs.SEND_WELCOME_EMAIL_BULK;
        this.confirmAction();
        break;
    }
  }

  openEnrollmentModal() {
    this.enrollModal.open();
    this.enrollmentQuestions = [
      new TagsQuestion({
        key: 'tags',
        label: 'nouns.tags',
        class: 'col-1',
        value: [],
        order: 1,
        options: {
          tagsLimit: 5
        }
      })
    ];
    this.enrollModal.open();
  }

  enrollSelectedStudents() {
    this.studentsToEnroll = [];

    this.selectedStudents.forEach((student) => {
      this.studentsToEnroll.push({ userId: student.id, userEmail: student.email });
    });
    this.openEnrollmentModal();
  }

  confirmEnrollModal() {
    this.enrollModal.close();
    const { tags } = this.dynamicForm.form.value;
    const tagsObject = tags.reduce((tagsObj, tag, index) => {
      tagsObj[`tag${index + 1}`] = tag;
      return tagsObj;
    }, {});
    const studentsToEnroll = this.studentsToEnroll.map((student) => {
      return { email: student.userEmail, ...tagsObject };
    });
    this.onEnroll.emit({ studentsToEnroll, hasToReEnroll: !this.options.filters.notTakenCourse });
  }

  openModal(action) {
    this.action = action;
    this.activateModal.open();
  }

  confirmModal() {
    this.confirmAction();
    this.activateModal.close();
  }

  confirmAction() {
    const data = {
      jobType: this.action
    };
    this.updateStudentBulk(data);
  }

  sendEmail(email) {
    const data: any = {
      body: email.body,
      title: email.title,
      fromName: email.fromName,
      jobType: this.jobs.SEND_PERSONALIZED_EMAIL_BULK
    };
    if (email.autosend) {
      // A copy of the personalized email will be sent to the admin
      data.userData = {
        id: this.user.id,
        email: this.user.email
      };
    }
    this.updateStudentBulk(data);
  }

  addTags(addedTags: any[]) {
    const data = {
      addedTags,
      removedTags: [],
      jobType: this.jobs.UPDATE_TAGS_BULK
    };
    this.updateStudentBulk(data);
  }

  removeTags(removedTags: any[]) {
    const data = {
      addedTags: [],
      removedTags,
      jobType: this.jobs.UPDATE_TAGS_BULK
    };

    this.updateStudentBulk(data);
  }

  private updateStudentBulk(data): void {
    const bulkData = {
      users: this.setSelectList(),
      query: {
        clientId: this.listClientId(),
        search: this.dataSource.search,
        ...this.studentsListService.getDatasourceFilters(this.dataSource)
      },
      ...data
    };
    if (this.isHeaderSelectedAll) {
      delete bulkData.users;
      if (this.excludedStudents.size) {
        bulkData.query.excludedUsers = [...this.excludedStudents].map((userId) => ({ _id: userId }));
      }
    } else {
      delete bulkData.query;
    }
    this.setSelectAll(false);
    this.studentsService.updateBulkStudents(bulkData).then(async (res: any) => {
      const jobSubscription = this.jobsService.manageJob(res.jobId, bulkData.jobType).subscribe((job) => {
        if (job.status === 'finished' || job.status === 'fatal-error') {
          jobSubscription.unsubscribe();
          this.dataSource.refreshData();
        }
      });
    });
  }

  private setSelectList(): Array<any> {
    return this.selectedStudents.map((student) => {
      return { _id: student.id };
    });
  }

  private listClientId(): string {
    if (this.dataSource.filters && this.dataSource.filters.clientId) {
      return this.dataSource.filters.clientId;
    }
    return this.clientId;
  }

  private filterActionItems(btnActionList: any[]): Array<any> {
    if (!this.showActions) {
      return btnActionList.filter((item) => item.key !== '3' && item.key !== '4');
    }
    return btnActionList;
  }
}
